import React, { useState } from "react";
import { Control, Controller, FieldError, RegisterOptions } from "react-hook-form";

import { UploadService } from "shared/services";

import { FileField } from "./components";
import { copy } from "shared/assets";
import { blurActiveElement } from "shared/utils";
import { IUpload } from "shared/services/UploadService";

import { FieldWrapperProps } from "../FieldWrapper";

export interface IFileUploadValue {
  id?: string;
  file?: IUpload; // file to upload
  originalURL?: string; // existing upload
  previewURL?: string; // preview of chosen file
  type?: string;
  error?: string;
  size?: number; // in bytes
}

export interface FileUploadProps extends FieldWrapperProps {
  control: Control;
  accept?: string;
  validation?: RegisterOptions;
  value: IFileUploadValue;
  required?: boolean;
  error?: FieldError | null;
  handleFileChange?: (file: File) => void;
  handleFilePreview?: (file: File) => string;
  handleFileClear?: () => void;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  name,
  value,
  handleFileChange,
  handleFilePreview,
  handleFileClear,
  control,
  validation,
  ...rest
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleUpload = async (file: File): Promise<IFileUploadValue> => {
    setIsLoading(true);

    try {
      const data = await UploadService.uploadFile(file);

      const previewURL = handleFilePreview && handleFilePreview(file);

      setIsLoading(false);
      handleFileChange && handleFileChange(file);

      return {
        file: data,
        previewURL,
        originalURL: value?.originalURL, // pass original url even after new file select
      };
    } catch (error) {
      setIsLoading(false);

      return {
        error: copy.errors.general,
      };
    }
  };

  const triggerUpdate = (ref: React.RefObject<HTMLInputElement>) => {
    // focus and then blur active element (hack to trigger blur and update)
    ref.current && ref.current.focus();
    blurActiveElement();
  };

  const handleClear = async (ref: React.RefObject<HTMLInputElement>) => {
    handleFileClear && (await handleFileClear());

    if (ref.current) {
      ref.current.value = "";
    }

    triggerUpdate(ref);
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={validation}
      defaultValue={value}
      render={({ onChange, onBlur, ref }) => (
        <FileField
          ref={ref}
          name={name}
          value={value}
          isLoading={isLoading}
          validation={validation}
          handleUpload={(file: File) =>
            new Promise(async resolve => {
              // continue with the upload if file is valid
              const value = await handleUpload(file);
              onChange(value);

              triggerUpdate(ref);

              resolve();
            })
          }
          handleBlur={onBlur}
          handleClear={() => {
            handleClear(ref);

            // show original file after clearing the selected file
            onChange(
              value?.originalURL
                ? {
                    originalURL: value?.originalURL,
                  }
                : null
            );
          }}
          handleDelete={() => {
            handleClear(ref);

            // set value to null to delete it
            onChange(null);
          }}
          {...rest}
        />
      )}
    />
  );
};
